import { CustomPredictionService } from './custom-autocomplete-predictions/custom-prediction-service';
import { AutocompletePrediction, ILatLongBounds } from '@bridebook/web/app-shared/lib/search/types';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { GooglePlacesService } from '@bridebook/web/app-shared/lib/search/utils/google-places-service';
import type { Slug } from '@bridebook/toolbox/src/types';

export interface IAutocompleteSuggestionRequest {
  input: string;
  includedPrimaryTypes?: ('geocode' | 'establishment' | 'administrative_area_level_2')[];
  includedRegionCodes?: string[];
  locationBias?: google.maps.places.LocationBias;
  locationRestriction?: google.maps.places.LocationRestriction;
  language?: string;
  region?: string;
  countryCode?: CountryCodes;
  bounds?: ILatLongBounds;
  category: Slug | '';
}

export class AutocompleteSuggestionService {
  constructor(
    private customPredictionService: CustomPredictionService,
    private placesService: GooglePlacesService,
  ) {}

  async getPredictions(request: IAutocompleteSuggestionRequest): Promise<{
    input: IAutocompleteSuggestionRequest['input'];
    category: IAutocompleteSuggestionRequest['category'];
    predictions: AutocompletePrediction[];
    googlePredictions: Record<string, google.maps.places.AutocompletePrediction>;
  }> {
    const isNumeric = /\d/.test(request.input);
    const isFrance = request.countryCode === CountryCodes.FR;

    if (isNumeric && isFrance) {
      const franceNumericResult = await this.getFranceNumericPredictions(
        request.input,
        request.bounds,
      );

      if (franceNumericResult) {
        return {
          input: request.input,
          category: request.category,
          predictions: franceNumericResult.combinedPredictions,
          googlePredictions: franceNumericResult.googlePredictionsMap,
        };
      }
    }

    const options = this.getPlaceAutocompleteOptions(request);
    const { predictions: googlePredictions } =
      await this.placesService.getSuggestionPredictions(options);

    const predictions = this.mergePredictionsWithCustoms(
      request.input,
      request.countryCode,
      googlePredictions,
    );

    return {
      input: request.input,
      category: request.category,
      predictions,
      googlePredictions: googlePredictions.reduce<
        Record<string, google.maps.places.AutocompletePrediction>
      >((acc, curr) => {
        acc[curr.place_id] = curr;
        return acc;
      }, {}),
    };
  }

  /**
   * Get predictions for France numeric search
   * French couples prefer searching by department, but Bridebook does not support department code searches.
   * Current region-based setup (13 regions) limits monetization. That's why we need extra logic for France to handle department (113 departments) searches.
   * Departments are numeric for example 71, 72, 73, etc. So as long as its is numeric, we can use this logic otherwise we fallback to the default logic.
   */
  private getFranceNumericPredictions = async (input: string, bounds?: ILatLongBounds) => {
    const options = this.getPlaceAutocompleteOptions({
      input,
      includedPrimaryTypes: ['administrative_area_level_2'],
      bounds,
      countryCode: CountryCodes.FR,
      includedRegionCodes: [CountryCodes.FR],
    });
    const { predictions: googlePredictions, suggestions } =
      await this.placesService.getSuggestionPredictions(options);

    // If no admin area predictions found, return and continue with the default logic
    if (!googlePredictions?.length) {
      return null;
    }

    const place = suggestions[0].placePrediction?.toPlace();
    await place?.fetchFields({
      fields: ['location'],
    });

    if (place?.location) {
      const geocodeOptions = this.getPlaceAutocompleteOptions({
        input,
        includedPrimaryTypes: ['geocode'],
        bounds: {
          sw: { lat: place.location.lat(), lon: place.location.lng() },
          ne: { lat: place.location.lat(), lon: place.location.lng() },
        },
        countryCode: CountryCodes.FR,
        includedRegionCodes: [CountryCodes.FR],
      });

      const { predictions: geocodePredictions } =
        await this.placesService.getSuggestionPredictions(geocodeOptions);

      const combined = [...googlePredictions, ...geocodePredictions];
      const combinedPredictions = this.mergePredictionsWithCustoms(
        input,
        CountryCodes.FR,
        combined,
      );
      const googlePredictionsMap = combined.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.place_id]: curr,
        }),
        {},
      );

      return {
        combinedPredictions,
        googlePredictionsMap,
      };
    }

    const predictions = this.mergePredictionsWithCustoms(input, CountryCodes.FR, googlePredictions);
    const googlePredictionsMap = googlePredictions.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.place_id]: curr,
      }),
      {},
    );

    return {
      combinedPredictions: predictions,
      googlePredictionsMap,
    };
  };

  private mergePredictionsWithCustoms(
    input: string,
    countryCode: string | undefined,
    googlePredictions: google.maps.places.AutocompletePrediction[],
  ): AutocompletePrediction[] {
    const normalizedInput = input.trim().toLowerCase();
    if (!normalizedInput.length) {
      return [];
    }

    const customPredictions = this.customPredictionService.getMatching(
      countryCode,
      normalizedInput,
    );

    const googlePredictionsDeduplicated = googlePredictions.filter((prediction) => {
      const exists = customPredictions.find((customPrediction) => {
        const mainTextMatch =
          prediction.structured_formatting.main_text === customPrediction.mainText;

        if (customPrediction.replaceByMainText) {
          return mainTextMatch;
        }

        const secondaryTextMatch =
          prediction.structured_formatting.secondary_text === customPrediction.secondaryText;

        return mainTextMatch && secondaryTextMatch;
      });

      return !exists;
    });

    return [...customPredictions, ...googlePredictionsDeduplicated];
  }

  private getPlaceAutocompleteOptions = (
    request: Omit<IAutocompleteSuggestionRequest, 'category'>,
  ): google.maps.places.AutocompleteRequest => {
    const includedPrimaryTypes = request.includedPrimaryTypes ?? ['geocode'];

    const result: google.maps.places.AutocompleteRequest = {
      input: request.input,
      includedPrimaryTypes,
      ...(request.includedRegionCodes && { includedRegionCodes: request.includedRegionCodes }),
      ...(request.locationRestriction && { locationRestriction: request.locationRestriction }),
      ...(request.language && { language: request.language }),
      ...(request.region && { region: request.region }),
      locationBias:
        request.locationBias ||
        (request.bounds //support old bounds format
          ? new google.maps.LatLngBounds(
              new google.maps.LatLng(request.bounds.sw.lat, request.bounds.sw.lon),
              new google.maps.LatLng(request.bounds.ne.lat, request.bounds.ne.lon),
            )
          : undefined),
    };

    if (
      includedPrimaryTypes.includes('geocode') ||
      includedPrimaryTypes.includes('establishment') ||
      includedPrimaryTypes.includes('administrative_area_level_2')
    ) {
      result.includedRegionCodes = request.countryCode ? [request.countryCode] : undefined;
    }

    return result;
  };
}
